import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Divider,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';

/**
 * TransferList Component - Dual List Box para seleção de itens
 * 
 * @param {Array} available - Lista de itens disponíveis (lado esquerdo)
 * @param {Array} selected - Lista de itens selecionados (lado direito)
 * @param {Function} onChange - Callback quando a seleção muda
 * @param {String} leftTitle - Título da lista esquerda
 * @param {String} rightTitle - Título da lista direita
 * @param {String} getItemLabel - Função para obter o label do item
 * @param {String} getItemValue - Função para obter o value do item
 * @param {Boolean} searchable - Habilita busca
 * @param {Array} originalItems - IDs dos itens que já existiam (para destacar visualmente)
 */
export const TransferList = ({
  available = [],
  selected = [],
  onChange,
  leftTitle = 'Disponíveis',
  rightTitle = 'Selecionados',
  getItemLabel = (item) => item.name || item.label || item,
  getItemValue = (item) => item._id || item.id || item,
  searchable = true,
  height = '400px',
  originalItems = [], // Novos: IDs dos itens originais
}) => {
  const [checked, setChecked] = useState([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');

  const leftChecked = checked.filter(value => 
    available.some(item => getItemValue(item) === value)
  );
  const rightChecked = checked.filter(value => 
    selected.some(item => getItemValue(item) === value)
  );

  // Filtra itens baseado na busca
  const filterItems = (items, searchTerm) => {
    if (!searchTerm) return items;
    return items.filter(item => 
      getItemLabel(item).toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredAvailable = filterItems(available, leftSearch);
  const filteredSelected = filterItems(selected, rightSearch);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleToggleAll = (items) => () => {
    const itemValues = items.map(getItemValue);
    
    if (itemValues.every(value => checked.includes(value))) {
      // Desmarca todos
      setChecked(checked.filter(value => !itemValues.includes(value)));
    } else {
      // Marca todos
      setChecked([...new Set([...checked, ...itemValues])]);
    }
  };

  const handleMoveRight = () => {
    const itemsToMove = available.filter(item => 
      leftChecked.includes(getItemValue(item))
    );
    const newSelected = [...selected, ...itemsToMove];
    const newAvailable = available.filter(item => 
      !leftChecked.includes(getItemValue(item))
    );

    onChange(newSelected, newAvailable);
    setChecked(checked.filter(value => !leftChecked.includes(value)));
  };

  const handleMoveLeft = () => {
    const itemsToMove = selected.filter(item => 
      rightChecked.includes(getItemValue(item))
    );
    const newAvailable = [...available, ...itemsToMove];
    const newSelected = selected.filter(item => 
      !rightChecked.includes(getItemValue(item))
    );

    onChange(newSelected, newAvailable);
    setChecked(checked.filter(value => !rightChecked.includes(value)));
  };

  const handleMoveAllRight = () => {
    const newSelected = [...selected, ...available];
    onChange(newSelected, []);
    setChecked([]);
  };

  const handleMoveAllLeft = () => {
    const newAvailable = [...available, ...selected];
    onChange([], newAvailable);
    setChecked([]);
  };

  const numberOfChecked = (items) => {
    const itemValues = items.map(getItemValue);
    return checked.filter(value => itemValues.includes(value)).length;
  };

  const customList = (title, items, searchValue, setSearchValue, isLeft = true) => (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        sx={{ 
          px: 2, 
          py: 1.5,
          backgroundColor: 'primary.main',
          color: 'white',
          '& .MuiCardHeader-action': {
            margin: 0,
          }
        }}
        avatar={
          <Checkbox
            sx={{ 
              color: 'white',
              '&.Mui-checked': {
                color: 'white',
              }
            }}
            onClick={handleToggleAll(items)}
            checked={items.length > 0 && numberOfChecked(items) === items.length}
            indeterminate={
              numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            inputProps={{
              'aria-label': 'Selecionar todos',
            }}
          />
        }
        title={
          <Typography variant="subtitle1" fontWeight="600">
            {title}
          </Typography>
        }
        subheader={
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {numberOfChecked(items)}/{items.length} selecionado(s)
          </Typography>
        }
      />
      <CardContent sx={{ 
        p: 0, 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        '&:last-child': { pb: 0 }
      }}>
        {searchable && (
          <Box sx={{ p: 2, pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        <Divider />
        <List
          sx={{
            flex: 1,
            overflow: 'auto',
            height: height,
            bgcolor: 'background.paper',
          }}
          dense
          component="div"
          role="list"
        >
          {items.length === 0 ? (
            <ListItem>
              <ListItemText 
                primary={
                  <Typography variant="body2" color="text.secondary" align="center">
                    Nenhum item disponível
                  </Typography>
                } 
              />
            </ListItem>
          ) : (
            items.map((item) => {
              const value = getItemValue(item);
              const labelId = `transfer-list-${isLeft ? 'left' : 'right'}-label-${value}`;
              const isOriginal = !isLeft && originalItems.includes(value);

              return (
                <ListItem
                  key={value}
                  role="listitem"
                  button
                  onClick={handleToggle(value)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    backgroundColor: isOriginal ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    borderLeft: isOriginal ? '3px solid' : 'none',
                    borderLeftColor: isOriginal ? 'primary.main' : 'transparent',
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.indexOf(value) !== -1}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    id={labelId} 
                    primary={getItemLabel(item)}
                    secondary={isOriginal ? 'Já estava no grupo' : null}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isOriginal ? 600 : 400,
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'primary.main',
                    }}
                  />
                </ListItem>
              );
            })
          )}
        </List>
      </CardContent>
    </Card>
  );

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
      <Grid item xs={12} md={5}>
        {customList(leftTitle, filteredAvailable, leftSearch, setLeftSearch, true)}
      </Grid>
      <Grid item xs={12} md={2}>
        <Stack
          direction={{ xs: 'row', md: 'column' }}
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ 
            minHeight: { md: '400px' },
            py: { xs: 2, md: 0 }
          }}
        >
          <Button
            sx={{ minWidth: { xs: '80px', md: '100%' } }}
            variant="outlined"
            size="small"
            onClick={handleMoveAllRight}
            disabled={available.length === 0}
            aria-label="Mover todos para direita"
            title="Mover todos para direita"
          >
            <KeyboardDoubleArrowRightIcon />
          </Button>
          <Button
            sx={{ minWidth: { xs: '80px', md: '100%' } }}
            variant="outlined"
            size="small"
            onClick={handleMoveRight}
            disabled={leftChecked.length === 0}
            aria-label="Mover selecionados para direita"
            title="Mover selecionados para direita"
          >
            <ChevronRightIcon />
          </Button>
          <Button
            sx={{ minWidth: { xs: '80px', md: '100%' } }}
            variant="outlined"
            size="small"
            onClick={handleMoveLeft}
            disabled={rightChecked.length === 0}
            aria-label="Mover selecionados para esquerda"
            title="Mover selecionados para esquerda"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            sx={{ minWidth: { xs: '80px', md: '100%' } }}
            variant="outlined"
            size="small"
            onClick={handleMoveAllLeft}
            disabled={selected.length === 0}
            aria-label="Mover todos para esquerda"
            title="Mover todos para esquerda"
          >
            <KeyboardDoubleArrowLeftIcon />
          </Button>
        </Stack>
      </Grid>
      <Grid item xs={12} md={5}>
        {customList(rightTitle, filteredSelected, rightSearch, setRightSearch, false)}
      </Grid>
    </Grid>
  );
};

TransferList.propTypes = {
  available: PropTypes.array,
  selected: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  leftTitle: PropTypes.string,
  rightTitle: PropTypes.string,
  getItemLabel: PropTypes.func,
  getItemValue: PropTypes.func,
  searchable: PropTypes.bool,
  height: PropTypes.string,
  originalItems: PropTypes.array,
};
